import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Phone,
  ShieldCheck,
  Sparkles,
  Play,
  MessageCircle,
  BookOpenCheck,
  Waypoints,
  ClipboardCheck,
  BarChart3,
  Users,
  Stethoscope,
  FlaskConical,
  BookOpen,
  Beaker,
  Volume2,
  Zap,
  Languages,
  ChevronRight,
} from "lucide-react";
import {
  loadSpeak,
  aggregateSpeak,
  buildDemoSpeak,
  L,
} from "@/lib/neuroSpeakStore";
import {
  BaselineAssessment,
  WordRetrieval,
  SentenceConstruction,
  PictureWordSentence,
} from "@/components/neurospeak/exercises";
import {
  CommunicateNow,
  MessageBuilder,
  VocabManager,
  AccessibilityPanel,
} from "@/components/neurospeak/aac";
import {
  CommunicationProfile,
  TaskAssistance,
  FunctionalScenarios,
  SpeakFeedbackModal,
  RecoveryJourneyRibbon,
} from "@/components/neurospeak/progress";

// -----------------------------------------------------------------------
// NeuroSpeak AI — third pillar (Chunk A: foundation + Journey dashboard)
// Chunks B–E will fill the training engines, AAC, profile charts, and
// cross-module integrations. All state is device-only (localStorage).
// -----------------------------------------------------------------------

const TABS = [
  { key: "journey", label: "Journey", Icon: Sparkles },
  { key: "baseline", label: "Baseline", Icon: ClipboardCheck },
  { key: "train", label: "Word Retrieval", Icon: Play },
  { key: "sentence", label: "Sentence", Icon: MessageCircle },
  { key: "pws", label: "Picture → Sentence", Icon: Waypoints },
  { key: "communicate", label: "Communicate Now", Icon: Volume2 },
  { key: "vocab", label: "My Words", Icon: BookOpenCheck },
  { key: "profile", label: "Progress", Icon: BarChart3 },
  { key: "caregiver", label: "Caregiver", Icon: Users },
  { key: "therapist", label: "Professional", Icon: Stethoscope },
  { key: "research", label: "Research", Icon: FlaskConical },
  { key: "how", label: "How it works", Icon: BookOpen },
  { key: "demo", label: "2-min Demo", Icon: Beaker },
];

const PLACEHOLDER = (label) => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
    <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
      {label}
    </div>
    <p className="mt-3 text-zinc-300 max-w-2xl">
      Coming next chunk. This module is wired in device-only storage and
      already tracks progress once implemented — the UI arrives in the next
      chunk of NeuroSpeak AI.
    </p>
  </div>
);

export default function NeuroSpeak() {
  const [state, setState] = useState(() => loadSpeak());
  const [tab, setTab] = useState("journey");
  const [demoActive, setDemoActive] = useState(false);
  const [feedback, setFeedback] = useState(false);

  const refresh = () => setState(loadSpeak());
  const openFeedback = () => setFeedback(true);
  const closeFeedback = () => {
    setFeedback(false);
    refresh();
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab]);

  const effective = demoActive ? buildDemoSpeak() : state;
  const agg = useMemo(() => aggregateSpeak(effective), [effective]);
  const lang = state.profile?.language || "en";
  const acc = state.profile?.accessibility || {};

  return (
    <div
      className={`min-h-screen bg-[#05050A] text-white pb-24 ${
        acc.large_text ? "text-lg" : ""
      } ${acc.high_contrast ? "contrast-125" : ""}`}
    >
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(45% 30% at 12% 4%, rgba(34,211,238,0.18) 0%, transparent 60%), radial-gradient(45% 30% at 95% 90%, rgba(52,211,153,0.16) 0%, transparent 60%)",
        }}
      />

      {/* Top bar */}
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="speak-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>

          <div className="flex items-center gap-2">
            {/* Language switcher (functional in Chunk A for headings) */}
            <div className="hidden sm:flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-1 py-1">
              <Languages className="w-3.5 h-3.5 text-zinc-400 ml-2" />
              {[
                { k: "en", l: "EN" },
                { k: "ta", l: "TA" },
                { k: "hi", l: "HI" },
              ].map((o) => (
                <button
                  key={o.k}
                  data-testid={`speak-lang-${o.k}`}
                  onClick={() => {
                    const s = loadSpeak();
                    s.profile = { ...s.profile, language: o.k };
                    localStorage.setItem(
                      "silentscan_neurospeak_v1",
                      JSON.stringify(s)
                    );
                    setState(s);
                  }}
                  className={`text-[11px] uppercase font-semibold rounded-full px-2 py-1 tracking-widest ${
                    lang === o.k
                      ? "bg-cyan-400 text-black"
                      : "text-zinc-300 hover:text-white"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            <a
              href="tel:108"
              data-testid="speak-emergency-108"
              className="inline-flex items-center gap-1.5 rounded-full bg-red-500 text-black text-xs font-semibold px-3 py-1.5"
            >
              <Phone className="w-3.5 h-3.5" />
              Emergency 108
            </a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-6 pt-10">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-cyan-300/80">
            Chapter 15 · NeuroSpeak AI
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02]"
          >
            Helping people find their words.
            <br />
            <span className="italic text-emerald-300">Again.</span>
          </motion.h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            An assistive communication and language-training prototype for
            people navigating word-finding difficulties after stroke or brain
            injury. Task-performance only — never a diagnosis.
          </p>

          {/* 3-pillar strip */}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <PillarPill to="/dashboard" color="#22d3ee" label="SilentScan AI" />
            <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />
            <PillarPill to="/neurorehab" color="#a78bfa" label="NeuroRehab" />
            <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />
            <PillarPill to="/neurospeak" color="#34d399" label="NeuroSpeak" active />
          </div>
        </div>

        {/* Safety banner — always visible */}
        <div
          data-testid="speak-safety-banner"
          className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3"
        >
          <ShieldCheck className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-100 leading-relaxed">
            {L("safety.banner", lang)}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8 border-b border-white/10 -mx-6 px-6">
          <div className="flex gap-1 overflow-x-auto no-scrollbar pb-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`speak-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 flex-shrink-0 rounded-t-lg px-3.5 py-2 text-xs font-medium transition-colors border-b-2 ${
                  tab === t.key
                    ? "text-white border-cyan-400"
                    : "text-zinc-400 border-transparent hover:text-white hover:border-white/20"
                }`}
              >
                <t.Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="mt-8">
          {tab === "journey" && (
            <>
              <JourneyPane agg={agg} state={effective} lang={lang} />
              <div className="mt-4">
                <RecoveryJourneyRibbon />
              </div>
              <AccessibilityPanel onChange={refresh} />
            </>
          )}
          {tab === "baseline" && (
            <BaselineAssessment onDone={() => { refresh(); openFeedback(); }} />
          )}
          {tab === "train" && (
            <WordRetrieval
              level={state.level || 1}
              onDone={refresh}
              onFeedback={openFeedback}
            />
          )}
          {tab === "sentence" && (
            <SentenceConstruction
              level={state.level || 1}
              onDone={refresh}
              onFeedback={openFeedback}
            />
          )}
          {tab === "pws" && (
            <PictureWordSentence onDone={refresh} onFeedback={openFeedback} />
          )}
          {tab === "communicate" && (
            <div>
              <CommunicateNow big={!!acc.simple_ui} />
              <div className="mt-4">
                <MessageBuilder />
              </div>
            </div>
          )}
          {tab === "vocab" && (
            <div>
              <VocabManager />
              <div className="mt-4">
                <FunctionalScenarios onFeedback={openFeedback} />
              </div>
            </div>
          )}
          {tab === "profile" && (
            <div>
              <CommunicationProfile state={effective} />
              <div className="mt-4">
                <TaskAssistance state={effective} />
              </div>
            </div>
          )}
          {tab === "caregiver" && (
            <CaregiverPane agg={agg} state={effective} refresh={refresh} />
          )}
          {tab === "therapist" && (
            <TherapistPane agg={agg} state={effective} />
          )}
          {tab === "research" && <ResearchPane agg={agg} />}
          {tab === "how" && <HowPane />}
          {tab === "demo" && (
            <DemoPane
              active={demoActive}
              onEnter={() => {
                setDemoActive(true);
                setTab("journey");
              }}
              onExit={() => setDemoActive(false)}
            />
          )}
        </div>
      </div>

      <SpeakFeedbackModal open={feedback} onClose={closeFeedback} />
    </div>
  );
}

// -----------------------------------------------------------------------
// PillarPill
// -----------------------------------------------------------------------
const PillarPill = ({ to, color, label, active = false }) => (
  <Link
    to={to}
    data-testid={`pillar-${label.toLowerCase()}`}
    className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors"
    style={{
      borderColor: active ? color : `${color}55`,
      background: active ? `${color}25` : "transparent",
      color: active ? "#fff" : color,
    }}
  >
    <span
      className="inline-block w-1.5 h-1.5 rounded-full"
      style={{ background: color }}
    />
    {label}
  </Link>
);

// -----------------------------------------------------------------------
// Journey pane — the landing dashboard "MY COMMUNICATION JOURNEY"
// -----------------------------------------------------------------------
const StatCard = ({ label, val, sub, color = "#67e8f9", testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
  >
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
      {label}
    </div>
    <div
      className="mt-1 font-display text-2xl font-semibold tabular-nums"
      style={{ color }}
    >
      {val}
    </div>
    {sub && <div className="mt-0.5 text-[11px] text-zinc-500">{sub}</div>}
  </div>
);

const JourneyPane = ({ agg }) => {
  const nothing = agg.total_sessions === 0;
  return (
    <div>
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
              My communication journey
            </div>
            <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
              Every word, every attempt.{" "}
              <span className="italic text-emerald-300">Progress.</span>
            </h3>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-cyan-100">
            <Zap className="w-3.5 h-3.5" />
            Level {agg.level}
          </div>
        </div>

        {nothing ? (
          <div className="mt-6 rounded-2xl border border-cyan-400/25 bg-cyan-400/5 p-5 text-sm text-cyan-100">
            No sessions yet. Start with the <b>Baseline</b> tab to record a
            short Communication Task Performance Profile, then move to{" "}
            <b>Word Retrieval</b> or <b>Picture → Sentence</b>. Or open{" "}
            <b>2-min Demo</b> to see how the module tracks progress with
            simulated data.
          </div>
        ) : null}

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-2">
          <StatCard
            label="Words practiced"
            val={agg.words_practiced}
            color="#22d3ee"
            testId="speak-stat-words-practiced"
          />
          <StatCard
            label="Words recognized"
            val={agg.words_recognized}
            color="#a78bfa"
            testId="speak-stat-words-recognized"
          />
          <StatCard
            label="Naming accuracy"
            val={`${agg.naming_accuracy}%`}
            color="#34d399"
            testId="speak-stat-naming"
          />
          <StatCard
            label="Sentence accuracy"
            val={`${agg.sentence_accuracy}%`}
            color="#f472b6"
            testId="speak-stat-sentence"
          />
          <StatCard
            label="Communication attempts"
            val={agg.communication_attempts}
            color="#67e8f9"
            testId="speak-stat-attempts"
          />
          <StatCard
            label="Successful responses"
            val={agg.successful_responses}
            color="#34d399"
            testId="speak-stat-success"
          />
          <StatCard
            label="Cue dependence"
            val={`${agg.cue_dependence}%`}
            color="#fbbf24"
            sub="Lower = more independent"
            testId="speak-stat-cue"
          />
          <StatCard
            label="Session streak"
            val={`${agg.streak}d`}
            color="#f87171"
            testId="speak-stat-streak"
          />
        </div>

        <div className="mt-6 text-xs text-zinc-500 leading-relaxed max-w-2xl">
          <b>Non-diagnostic.</b> Numbers reflect task-performance in this
          module only and are not clinical measurements. Clinical
          interpretation must be performed by a qualified speech-language
          pathologist.
        </div>
      </div>

      {/* Recovery journey pillar map */}
      <div className="mt-4 rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          My recovery journey
        </div>
        <h3 className="mt-1 font-display text-xl sm:text-2xl font-semibold">
          Prevent → Assess → Rehabilitate → Communicate.
        </h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <PillarCard
            to="/dashboard"
            title="SilentScan AI"
            desc="Preventive health awareness"
            color="#22d3ee"
          />
          <PillarCard
            to="/neurorehab"
            title="NeuroRehab"
            desc="Cognitive rehabilitation support"
            color="#a78bfa"
          />
          <PillarCard
            to="/neurospeak"
            title="NeuroSpeak"
            desc="Communication rehabilitation support"
            color="#34d399"
            active
          />
        </div>
      </div>
    </div>
  );
};

const PillarCard = ({ to, title, desc, color, active = false }) => (
  <Link
    to={to}
    data-testid={`pillar-card-${title.toLowerCase()}`}
    className="block rounded-2xl border p-4 hover:bg-white/5 transition-colors"
    style={{
      borderColor: active ? color : "rgba(255,255,255,0.1)",
      background: active ? `${color}12` : "transparent",
    }}
  >
    <div className="flex items-center gap-2">
      <span
        className="inline-block w-2 h-2 rounded-full"
        style={{ background: color }}
      />
      <span
        className="text-[10px] uppercase tracking-widest font-semibold"
        style={{ color }}
      >
        {title}
      </span>
    </div>
    <div className="mt-2 text-sm text-white">{desc}</div>
  </Link>
);

// -----------------------------------------------------------------------
// How pane
// -----------------------------------------------------------------------
const HowPane = () => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
    <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
      How NeuroSpeak works
    </div>
    <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
      Assess, cue, retrieve, speak.
    </h3>
    <ol className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
      {[
        { t: "1. Baseline", b: "A short Communication Task Performance Profile — retrieval, recognition, sentences, functional." },
        { t: "2. Graduated cueing", b: "0 → 1 (semantic) → 2 (first letter) → 3 (multiple choice) → 4 (model). Independence is measured, not just accuracy." },
        { t: "3. Adaptive engine", b: "Rule-based level shift on the last 10 sessions — never invents medical treatment." },
        { t: "4. Communicate Now", b: "AAC mode with categories, saved phrases, text-to-speech. For when speech is difficult." },
        { t: "5. Progress", b: "Naming, sentence and cue-dependence trends over time. Task-performance only." },
        { t: "6. Safety", b: "Non-diagnostic language, always-visible Emergency 108, explicit consent for caregiver/professional share." },
      ].map((r) => (
        <li key={r.t} className="rounded-2xl border border-white/10 bg-black/30 p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
            {r.t}
          </div>
          <p className="mt-1 text-sm text-zinc-300 leading-relaxed">{r.b}</p>
        </li>
      ))}
    </ol>
  </div>
);

// -----------------------------------------------------------------------
// Demo pane — 2-min impact demo (populates simulated data)
// -----------------------------------------------------------------------
const DemoPane = ({ active, onEnter, onExit }) => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
    <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
      2-minute impact demo
    </div>
    <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
      From struggling to <span className="italic text-emerald-300">communicating.</span>
    </h3>
    <p className="mt-3 text-sm text-zinc-300 max-w-2xl leading-relaxed">
      Populates 21 days of simulated word-retrieval, sentence and cue data so
      you can walk a judge through the Journey and Progress dashboards.
      Clearly labelled <b>DEMO DATA — NOT CLINICAL RESULTS</b>.
    </p>
    <div className="mt-5 flex flex-wrap items-center gap-2">
      {!active ? (
        <button
          data-testid="speak-demo-start"
          onClick={onEnter}
          className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-black font-medium px-5 py-2.5 text-sm hover:bg-amber-300"
        >
          <Beaker className="w-4 h-4" /> Start demo
        </button>
      ) : (
        <button
          data-testid="speak-demo-exit"
          onClick={onExit}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-5 py-2.5 text-sm text-white"
        >
          Exit demo
        </button>
      )}
    </div>
    {active && (
      <div className="mt-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs uppercase tracking-widest text-amber-200 font-semibold">
        Demo data active · not clinical results
      </div>
    )}
  </div>
);


// -----------------------------------------------------------------------
// Caregiver / Therapist / Research panes — consent-gated summaries
// -----------------------------------------------------------------------

const CaregiverPane = ({ agg, state }) => {
  const [consent, setConsent] = useState(false);
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
        Caregiver view · consent-gated
      </div>
      <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
        Share progress with someone you trust.
      </h3>
      <p className="mt-2 text-xs text-zinc-500 max-w-2xl">
        Caregivers can only see NeuroSpeak activity after you tick the consent
        box below. Sensitive notes and personal vocabulary stay hidden until
        you specifically enable them. You can revoke access at any time.
      </p>

      <label className="mt-4 inline-flex items-center gap-2 text-sm text-zinc-200 cursor-pointer">
        <input
          type="checkbox"
          data-testid="speak-caregiver-consent"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="accent-cyan-400"
        />
        I consent to sharing task performance with my caregiver.
      </label>

      {consent ? (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <SumRow label="Sessions completed" val={agg.total_sessions} />
          <SumRow label="Streak" val={`${agg.streak} days`} />
          <SumRow label="Naming accuracy" val={`${agg.naming_accuracy}%`} />
          <SumRow label="Cue dependence" val={`${agg.cue_dependence}%`} />
          <SumRow label="Saved communication phrases" val={(state.messages || []).length} />
          <SumRow label="Personalized vocabulary" val={Object.values(state.vocab || {}).reduce((a, l) => a + l.length, 0)} />
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-white/10 bg-black/30 p-5 text-sm text-zinc-400">
          Consent required — no data will be shown.
        </div>
      )}
    </div>
  );
};

const TherapistPane = ({ agg, state }) => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
    <div className="text-[10px] uppercase tracking-widest text-red-300 font-semibold">
      Professional dashboard · SLP view
    </div>
    <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
      Task history for a qualified professional.
    </h3>
    <p className="mt-2 text-xs text-zinc-500 max-w-2xl">
      Clinical interpretation must be performed by a qualified speech-language
      pathologist. NeuroSpeak does not diagnose aphasia.
    </p>

    <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
      <SumRow label="Sessions" val={agg.total_sessions} />
      <SumRow label="Words practiced" val={agg.words_practiced} />
      <SumRow label="Naming acc." val={`${agg.naming_accuracy}%`} />
      <SumRow label="Sentence acc." val={`${agg.sentence_accuracy}%`} />
      <SumRow label="Cue dep." val={`${agg.cue_dependence}%`} />
      <SumRow label="Level" val={agg.level} />
      <SumRow label="Feedback logs" val={(state.feedback || []).length} />
      <SumRow label="Streak" val={`${agg.streak}d`} />
    </div>

    <div className="mt-5 rounded-2xl border border-white/10 bg-black/30 p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
        Recent 8 sessions
      </div>
      <div className="grid grid-cols-1 gap-1 text-xs text-zinc-300">
        {(state.sessions || []).slice(-8).reverse().map((x, i) => (
          <div key={i} className="flex items-center justify-between border-b border-white/5 py-1">
            <span>
              {(x.at || "").slice(0, 10)} · <span className="text-white">{x.kind}</span>
              {x.word ? <span className="text-zinc-500"> · {x.word}</span> : null}
            </span>
            <span className={x.correct ? "text-emerald-300" : "text-amber-300"}>
              {x.correct ? "correct" : "attempt"} · cue {x.cue_level || 0}
            </span>
          </div>
        ))}
        {(state.sessions || []).length === 0 && (
          <div className="text-zinc-500">No sessions yet.</div>
        )}
      </div>
    </div>
  </div>
);

const ResearchPane = ({ agg }) => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
    <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
      Research mode · transparency
    </div>
    <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
      What we know · testing · don&apos;t yet know.
    </h3>
    <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="rounded-2xl border border-emerald-400/25 bg-emerald-400/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          What we know
        </div>
        <p className="mt-2 text-xs text-zinc-300 leading-relaxed">
          Structured, repeated word-retrieval practice with graduated cueing is a
          well-established rehabilitation technique used by clinicians. Task
          performance can be measured with cue-level and accuracy.
        </p>
      </div>
      <div className="rounded-2xl border border-cyan-400/25 bg-cyan-400/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
          What we are testing
        </div>
        <p className="mt-2 text-xs text-zinc-300 leading-relaxed">
          Whether a personalised, self-directed adaptive engine can produce
          measurable improvements on selected communication tasks over time —
          in a device-only, non-clinical setting.
        </p>
      </div>
      <div className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
          What we don&apos;t yet know
        </div>
        <p className="mt-2 text-xs text-zinc-300 leading-relaxed">
          Whether these on-device improvements translate to real-world
          communication. Whether adherence remains high over months. Whether
          the module is useful across the full spectrum of communication
          difficulty. No clinical claims.
        </p>
      </div>
    </div>

    <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
      <SumRow label="Naming acc." val={`${agg.naming_accuracy}%`} />
      <SumRow label="Sentence acc." val={`${agg.sentence_accuracy}%`} />
      <SumRow label="Cue dep." val={`${agg.cue_dependence}%`} />
      <SumRow label="Adherence" val={`${agg.streak}d`} />
    </div>

    <div className="mt-5 rounded-2xl border border-white/10 bg-black/30 p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
        Expert review · placeholders
      </div>
      <ul className="mt-2 text-xs text-zinc-400 space-y-1.5">
        <li>· Speech-language pathologist review — pending</li>
        <li>· Rehabilitation professional review — pending</li>
        <li>· Accessibility review — pending</li>
        <li>· Safety review — pending</li>
      </ul>
      <p className="mt-2 text-[11px] text-zinc-500">
        No expert names, credentials or testimonials are shown until they are
        formally provided. Prototype-grade module.
      </p>
    </div>
  </div>
);

const SumRow = ({ label, val }) => (
  <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
      {label}
    </div>
    <div className="mt-1 font-display text-xl font-semibold tabular-nums text-white">
      {val}
    </div>
  </div>
);
